import React from 'react';

import { showModal } from 'modal.react.js';
import { useFormContext } from 'react-hook-form';

import { useFetchCountries } from 'components/Pages/Admin/Itineraries/shared/hooks/useFetchCountries';
import Preview from 'components/Pages/Admin/shared/Preview';
import { FormFieldWrapper } from 'components/shared/BaseFormComponents';
import area from 'components/shared/GlobalModalArea/area';
import Modal from 'components/shared/GlobalModalArea/Modal';
import { Select } from 'components/shared/Select/adapters/HookForm';
import { SafariContentOption } from 'components/shared/Select/options/SafariContent';
import { isNotDefined } from 'utils/ramda';

import sharedStyles from '../../../../../shared/index.module.css';
import { useFetchCountyAreas } from '../../../../useFetchCountyAreas';
import { NameSection, TagsSection } from '../../../shared';
import * as types from '../../../types';

function ActivityForm() {
  const methods = useFormContext<types.ActivityFormValues>();
  const { control, watch, formState } = methods;
  const countriesQueryRes = useFetchCountries();
  const handledCountries =
    countriesQueryRes.data?.data.map((item) => ({ value: item.id.toString(), label: item.name })) || [];

  const countryId = watch('countryId');
  const { countryAreasQueryRes, handledAreas } = useFetchCountyAreas(countryId);

  React.useEffect(() => {
    if (countryId && formState.isDirty) methods.setValue('area', null);
  }, [countryId]);

  const openPagePreview = async (pageId: number) => {
    await showModal(area, (res) => (
      <Modal noPadding size="lg" isOpen toClose={res} stopMouseDownPropagation>
        <Preview pageId={pageId} getIsSamplePreview={() => false} />
      </Modal>
    ));
  };

  const [lockMenu, setLockMenu] = React.useState(false);
  const ActivityOptionMemoized = React.useCallback((props: any) => {
    return (
      <SafariContentOption
        {...props}
        hasPreview={!!props.data.optionActionAvailable}
        onPreview={async () => {
          if (!openPagePreview) return;
          setLockMenu(true);
          await openPagePreview(props.data.pageId);
          setLockMenu(false);
        }}
        hasContentIndicator={props.data.showContentIndicator}
        shared={props.data?.shared}
        subtitle={props.data.tagsString}
      />
    );
  }, []);

  return (
    <div>
      <div className={sharedStyles.sectionContainer}>
        <NameSection control={control}>I. Page Details</NameSection>

        <div className={sharedStyles.gridFieldContainer}>
          <div className={sharedStyles.gridSelectorContainer}>
            <Select
              name="countryId"
              control={control}
              label={() => 'Select Country'}
              options={handledCountries}
              getOptionLabel={(option) => option?.label}
              getOptionValue={(option) => option?.value}
              isLoading={countriesQueryRes.isLoading}
              optionToString
              isClearable
              blurInputOnSelect
            />
          </div>

          <div className={sharedStyles.gridSelectorContainer}>
            <FormFieldWrapper errorMessage={formState.errors.area?.name?.message}>
              <Select
                key={countryId}
                name="area"
                control={control}
                label={() => 'Select Area'}
                options={handledAreas}
                isDisabled={isNotDefined(countryId)}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option.id}
                isLoading={countryAreasQueryRes.isFetching}
                blurInputOnSelect
                lockMenu={lockMenu}
                components={{ Option: ActivityOptionMemoized }}
                isClearable
                creatable
                onCreateOption={(inputValue) => ({ name: inputValue, id: null, confirmed: false }) as any}
              />
            </FormFieldWrapper>
          </div>
        </div>
      </div>

      <TagsSection methods={methods} />
    </div>
  );
}

export { ActivityForm };
